import { useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Stamp } from "@/components/common";
import { useCollection, FormDialog } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { fmtDate } from "@/lib/api";

/** Generic ledger-style collection page driven by config. */
export default function CollectionPage({ coll, permKey, eyebrow, title, description, columns, fields, params = {}, searchKeys = ["name", "title"], allowCreateAll, transform }) {
  const { can, user } = useAuth();
  const { rows, loading, create, update, remove } = useCollection(coll, params);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const editable = can(permKey, "edit") || allowCreateAll;
  const filtered = rows.filter((r) => !q || searchKeys.some((k) => String(r[k] || "").toLowerCase().includes(q.toLowerCase())));
  const grid = `minmax(0,2fr) ${columns.slice(1).map(() => "minmax(0,1fr)").join(" ")} ${editable ? "72px" : ""}`;

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={eyebrow} title={title} description={description}
        actions={editable && <Button data-testid={`${coll}-new-button`} className="rounded-full" onClick={() => { setEditing(null); setOpen(true); }}><Plus size={16} className="mr-1" /> New</Button>} />
      <div className="paper-card overflow-hidden rise">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-secondary/40">
          <Input data-testid={`${coll}-search-input`} placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs h-9 bg-card" />
          <span className="font-mono text-xs text-muted-foreground">{filtered.length} records</span>
        </div>
        <div className="ledger-row bg-card text-[11px] font-mono uppercase tracking-wider text-muted-foreground !py-2" style={{ gridTemplateColumns: grid }}>
          {columns.map((c) => <div key={c.key}>{c.label}</div>)}{editable && <div />}
        </div>
        {loading ? <div className="p-8 text-sm text-muted-foreground">Loading…</div> : filtered.length === 0 ? <EmptyState /> : (
          <div className="stagger">
            {filtered.map((r) => (
              <div key={r.id} className="ledger-row" style={{ gridTemplateColumns: grid }} data-testid={`${coll}-row-${r.id}`}>
                {columns.map((c, i) => <div key={c.key} className={i === 0 ? "font-medium truncate" : "text-sm text-muted-foreground truncate"}>{c.render ? c.render(r) : renderCell(r[c.key], c)}</div>)}
                {editable && (
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`${coll}-edit-${r.id}`} onClick={() => { setEditing(r); setOpen(true); }}><Pencil size={14} /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" data-testid={`${coll}-delete-${r.id}`} onClick={async () => { await remove(r.id); toast.success("Deleted"); }}><Trash2 size={14} /></Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <FormDialog open={open} onOpenChange={setOpen} title={editing ? `Edit ${title}` : `New ${title}`} fields={fields} initial={editing || defaults(fields)} testId={`${coll}-form-dialog`}
        onSubmit={async (f) => { const body = transform ? transform(f, user) : f; if (editing) await update(editing.id, body); else await create(body); toast.success(editing ? "Updated" : "Created"); }} />
    </div>
  );
}

const defaults = (fields) => Object.fromEntries(fields.filter((f) => f.default !== undefined).map((f) => [f.name, f.default]));

function renderCell(v, c) {
  if (c.type === "stamp") return <Stamp status={v} />;
  if (c.type === "date") return fmtDate(v);
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object" && v) return JSON.stringify(v);
  return v ?? "—";
}
